/**
 * Subscription Purchase Modal
 * Handles subscription creation with Stripe Payment Elements
 */

import React, { useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { stripePromise } from '@/lib/stripe';
import { getErrorMessage } from '@/lib/utils';

interface SubscriptionPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  tier: {
    name: string;
    tier: 'PRO' | 'ENTERPRISE';
    price: string;
    credits: number;
  } | null;
  clientSecret: string | null;
}

const CheckoutForm: React.FC<{
  onSuccess: () => void;
  onCancel: () => void;
  tier: { name: string; tier: 'PRO' | 'ENTERPRISE'; price: string; credits: number };
}> = ({ onSuccess, onCancel, tier }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setErrorMessage('Stripe is not ready. Please try again.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // Submit the payment elements to ensure they're ready
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message || 'Payment validation failed');
        setIsProcessing(false);
        return;
      }

      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/billing?subscription=success`,
        },
        redirect: 'if_required',
      });

      if (error) {
        setErrorMessage(error.message || 'Payment failed');
        setIsProcessing(false);
      } else {
        // Payment succeeded
        onSuccess();
      }
    } catch (err: unknown) {
      setErrorMessage(getErrorMessage(err) || 'An unexpected error occurred');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Subscription Summary */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-sm text-gray-600">Subscribing to</p>
            <p className="text-lg font-bold text-gray-900">{tier.name} Plan</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Monthly</p>
            <p className="text-2xl font-bold text-primary">{tier.price}/mo</p>
          </div>
        </div>
        <div className="pt-2 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Includes <span className="font-semibold">{tier.credits} credits</span> per month
          </p>
        </div>
      </div>

      {/* Payment Element */}
      <div>
        <PaymentElement 
          onReady={() => setIsReady(true)}
          options={{
            layout: 'tabs',
          }}
        />
      </div>

      {!isReady && !isProcessing && (
        <div className="flex items-center justify-center py-4">
          <LoadingSpinner size="sm" />
          <span className="ml-2 text-sm text-gray-600">Loading payment form...</span>
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="px-4 py-3 bg-error-50 border border-error-200 rounded-lg">
          <p className="text-sm text-error-600">{errorMessage}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          disabled={isProcessing}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || !isReady || isProcessing}
          className="px-6 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
        >
          {isProcessing && <LoadingSpinner size="sm" />}
          {isProcessing ? 'Processing...' : !isReady ? 'Loading...' : 'Subscribe Now'}
        </button>
      </div>
    </form>
  );
};

export const SubscriptionPurchaseModal: React.FC<SubscriptionPurchaseModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  tier,
  clientSecret,
}) => {
  if (!isOpen || !tier || !clientSecret) {
    return null;
  }

  const options = {
    clientSecret,
  };

  const handleSuccess = () => {
    onSuccess();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Subscribe to {tier.name}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <Elements stripe={stripePromise} options={options}>
            <CheckoutForm onSuccess={handleSuccess} onCancel={onClose} tier={tier} />
          </Elements>
        </div>
      </div>
    </div>
  );
};

